import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { T, FormCard, Toast } from './theme';

const API_BASE = 'https://d3bdqxkpn753kf.cloudfront.net';

const emptyForm = { category: '', title: '', youtube_url: '', author: '', visibility: 'PUBLIC' };

const CreateVideoSection = () => {
  const { role } = useAuth();
  const [categories, setCategories] = useState([]);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [formType, setFormType] = useState('existing');
  const [addForm, setAddForm] = useState({ ...emptyForm });
  const [newForm, setNewForm] = useState({ ...emptyForm });

  const userEmail = localStorage.getItem('vrvEmail') || '';
  const roles = (role || '').split(',').map(s => s.trim().toUpperCase());
  const isAdmin = roles.includes('ADMIN');
  const canDirectPublish = isAdmin || (roles.includes('CONTENT-CREATOR') && roles.includes('CONTENT-APPROVER'));

  useEffect(() => {
    fetch(`${API_BASE}/vrv-youtubecontent-getAll`)
      .then(r => r.json())
      .then(data => {
        const parsed = typeof data.body === 'string' ? JSON.parse(data.body) : data;
        const cats = [...new Set((parsed.data || []).map(v => v.category).filter(Boolean))];
        setCategories(cats);
        if (cats.length === 0) setFormType('new');
      }).catch(() => {});
  }, []);

  const showMsg = (message, type) => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 3500);
  };

  const handleSubmit = async (e, form, isNew) => {
    e.preventDefault();
    if (!form.category || !form.youtube_url || !form.author || !form.title) { showMsg('Please fill all required fields', 'error'); return; }
    try {
      const token = localStorage.getItem('vrvToken');
      const res = await fetch(`${API_BASE}/vrv-youtubecontent-create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...form, createdBy: userEmail, createdAt: new Date().toISOString(), status: canDirectPublish ? 'APPROVED' : 'PENDING', visibility: canDirectPublish ? form.visibility : 'PRIVATE' }),
      });
      const data = await res.json();
      if (res.ok) {
        showMsg(isNew ? 'New category & video created!' : 'Video added successfully!', 'success');
        if (isNew) setNewForm({ ...emptyForm }); else setAddForm({ ...emptyForm });
      } else showMsg(data.message || data.error || 'Failed', 'error');
    } catch { showMsg('Error connecting to server', 'error'); }
  };

  const VisibilityField = ({ form, setForm }) => canDirectPublish ? (
    <div>
      <label className="vrv-label">Visibility</label>
      <select value={form.visibility} onChange={e => setForm({ ...form, visibility: e.target.value })} className="vrv-select">
        <option value="PUBLIC">🌐 Public</option>
        <option value="PRIVATE">🔒 Private</option>
      </select>
    </div>
  ) : null;

  return (
    <div>
      {/* Type switcher pills */}
      <div className="flex gap-2 mb-6">
        {[
          { id: 'existing', label: '📂 Add to Existing Category', disabled: categories.length === 0 },
          { id: 'new',      label: '➕ Create New Category' },
        ].map(opt => {
          const active = formType === opt.id;
          return (
            <button key={opt.id} onClick={() => setFormType(opt.id)} disabled={opt.disabled}
              className="px-5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wide transition-all duration-200 disabled:opacity-40"
              style={{
                fontFamily: 'Poppins, sans-serif',
                background: active ? `linear-gradient(135deg, ${T.navy}, ${T.navyDark})` : 'white',
                color: active ? 'white' : T.muted,
                border: active ? 'none' : '1.5px solid rgba(0,0,0,0.08)',
                boxShadow: active ? '0 4px 14px rgba(22,33,62,0.2)' : 'none',
              }}>
              {opt.label}
            </button>
          );
        })}
      </div>

      {formType === 'existing' && (
        <FormCard title="Add to Existing Category" subtitle="Video Content">
          <form onSubmit={e => handleSubmit(e, addForm, false)} className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="vrv-label">Category *</label>
              <select value={addForm.category} onChange={e => setAddForm({ ...addForm, category: e.target.value })} className="vrv-select" required>
                <option value="">Select Category</option>
                {categories.map((cat, i) => <option key={i} value={cat}>{cat}</option>)}
              </select>
            </div>
            <div>
              <label className="vrv-label">Title *</label>
              <input type="text" value={addForm.title} onChange={e => setAddForm({ ...addForm, title: e.target.value })} placeholder="Video title" className="vrv-input" required />
            </div>
            <div>
              <label className="vrv-label">YouTube URL *</label>
              <input type="url" value={addForm.youtube_url} onChange={e => setAddForm({ ...addForm, youtube_url: e.target.value })} placeholder="https://youtu.be/…" className="vrv-input" required />
            </div>
            <div>
              <label className="vrv-label">Author *</label>
              <input type="text" value={addForm.author} onChange={e => setAddForm({ ...addForm, author: e.target.value })} placeholder="Author name" className="vrv-input" required />
            </div>
            <VisibilityField form={addForm} setForm={setAddForm} />
            <div className="flex items-end">
              <button type="submit" className="btn-gold">{canDirectPublish ? 'Add Video →' : 'Send for Approval →'}</button>
            </div>
          </form>
        </FormCard>
      )}

      {formType === 'new' && (
        <FormCard title="Create New Category & Video" subtitle="New Category">
          <form onSubmit={e => handleSubmit(e, newForm, true)} className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="vrv-label">New Category Name *</label>
              <input type="text" value={newForm.category} onChange={e => setNewForm({ ...newForm, category: e.target.value })} placeholder="e.g. Bhajans" className="vrv-input" required />
            </div>
            <div>
              <label className="vrv-label">Title *</label>
              <input type="text" value={newForm.title} onChange={e => setNewForm({ ...newForm, title: e.target.value })} placeholder="Video title" className="vrv-input" required />
            </div>
            <div>
              <label className="vrv-label">YouTube URL *</label>
              <input type="url" value={newForm.youtube_url} onChange={e => setNewForm({ ...newForm, youtube_url: e.target.value })} placeholder="https://youtu.be/…" className="vrv-input" required />
            </div>
            <div>
              <label className="vrv-label">Author *</label>
              <input type="text" value={newForm.author} onChange={e => setNewForm({ ...newForm, author: e.target.value })} placeholder="Author name" className="vrv-input" required />
            </div>
            <VisibilityField form={newForm} setForm={setNewForm} />
            <div className="flex items-end">
              <button type="submit" className="btn-gold">{canDirectPublish ? 'Create & Add →' : 'Send for Approval →'}</button>
            </div>
          </form>
        </FormCard>
      )}

      <Toast toast={toast} onClose={() => setToast({ show: false, message: '', type: '' })} />
    </div>
  );
};

export default CreateVideoSection;
